import { newId } from "@codesandbox/pitcher-common";
import { getCommandIcon } from "commands/utils/icons";
import { useEditorStore } from "components/Editor/useEditorStore";
import { ErrorBoundary } from "components/Error/ErrorBoundary";
import { useRegisterCommands } from "components/Layout/Panels/VSCode/useRegisterCommands";
import { useCopyToClipboard } from "features/common/useCopyToClipboard";
import { useEditor } from "features/editor/Feature";
import { useSession } from "hooks/api/useSession";
import { Box, Button, Popover, Stack, Tooltip } from "prism-react";
import React, { useState } from "react";

import { LiveIcon, LiveSessionSettings } from "./LiveSessionSettings";
import { ShareRepositoryProject } from "./ShareRepositoryProject";
import { ShareSandbox } from "./ShareSandbox";

const copyIcon = getCommandIcon("copy");
export const ShareButton: React.FC = () => {
  const [session] = useSession();
  const isAuthenticated = session.state === "AUTHENTICATED";
  const editor = useEditor();

  const [editorURL] = useState(() => {
    const url = new URL(window.location.href);

    if (editor.isPrivate) {
      return url;
    }

    // In public repos we do not want to share with a workspaceId, as team members
    // will be inferred to this workspace and external users will now open the public
    // version
    url.searchParams.delete("workspaceId");
    // Just to clean up
    url.searchParams.delete("preventWorkspaceRedirect");

    return url;
  });

  const isSandbox = editor.type === "sandbox";
  const isPrivateDraft =
    editor.type === "sandbox" && editor.draft && editor.privacy === 2;
  const shouldRenderLiveSession =
    editor.authorization === "owner" && !isPrivateDraft;
  const canCreateLiveSession = editor.allowedCapabilities.createLiveSession;

  const copy = useCopyToClipboard(undefined, true);
  const baseCommands = [
    {
      icon: copyIcon,
      id: newId(),
      keywords: "share invite",
      name: "Copy project URL",
      perform: () => copy(window.location.href),
      section: "Share",
    },
    {
      icon: copyIcon,
      id: newId(),
      keywords: "share invite",
      name: "Copy workspace URL",
      perform: () => copy(editorURL.toString()),
      section: "Share",
      subtitle: "For sharing files & DevTools",
    },
  ];

  useRegisterCommands(baseCommands, [editorURL]);
  const { liveSession } = useEditorStore();

  if (liveSession.state.role === "GUEST") {
    return null;
  }

  const iconType = (() => {
    if (editor.type === "branch") {
      return editor.isPrivate ? "lockClose" : "community";
    }

    if (editor.privacy === 2) {
      return "lockClose";
    } else if (editor.privacy === 1) {
      return "eyeClose";
    }

    return "community";
  })();

  const component =
    !isAuthenticated && isSandbox ? (
      <Tooltip content="Sign in to access sharing options">
        <Button variant="secondary" disabled>
          Share
        </Button>
      </Tooltip>
    ) : (
      <Popover
        contentProps={{ align: "end" }}
        padding={"none"}
        trigger={
          liveSession.state.status === "CONNECTED" ? (
            <Button variant="secondary">
              {/** paddingleft to ensure no UI jumps between the regular icon size and the live dot */}
              <Stack css={{ paddingLeft: "$1" }} horizontal>
                <LiveIcon size={3} type="circleFull" connected />
                <span>Share</span>
              </Stack>
            </Button>
          ) : (
            <Button icon={iconType} iconSize={4} variant="secondary">
              Share
            </Button>
          )
        }
      >
        <ErrorBoundary context="SHARE_MODAL">
          <Stack
            css={{
              width: shouldRenderLiveSession ? 740 : 370,
              alignItems: "stretch",
            }}
            gap={0}
            horizontal
          >
            <Box css={{ width: shouldRenderLiveSession ? "50%" : "100%" }}>
              {isSandbox ? (
                <ShareSandbox editorURL={editorURL} sandboxId={editor.id} />
              ) : (
                <ShareRepositoryProject editorURL={editorURL} />
              )}
            </Box>

            {shouldRenderLiveSession && (
              <Box css={{ width: "50%" }}>
                <LiveSessionSettings disabled={!canCreateLiveSession} />
              </Box>
            )}
          </Stack>
        </ErrorBoundary>
      </Popover>
    );

  return component;
};
